import type { MqttClient } from "mqtt";
import { useEffect, useRef, useState } from "react";
import { config } from "./config";
import type { RealtimeEvent } from "./types";

export type RealtimeStatus = "disabled" | "connecting" | "connected" | "error";

/**
 * Subscribes to a realtime topic over MQTT/WebSocket on AWS IoT using the
 * SST custom authorizer; the Cognito access token travels as the MQTT
 * password. TV mode and the admin dashboard read `factory`, which carries
 * every event; issue pages read `issues`. Falls back to polling (handled by
 * the caller) when disabled.
 */
export function useRealtime(
  token: string | undefined,
  channel: "factory" | "issues",
  onEvent: (event: RealtimeEvent) => void,
): RealtimeStatus {
  const [status, setStatus] = useState<RealtimeStatus>(config.realtime.endpoint ? "connecting" : "disabled");
  const handler = useRef(onEvent);
  handler.current = onEvent;

  useEffect(() => {
    if (!config.realtime.endpoint || !config.realtime.authorizer) return;
    if (!config.authDisabled && !token) return;
    let client: MqttClient | undefined;
    let cancelled = false;
    const topic = `${config.realtime.topicPrefix}/${channel}`;
    const url = `wss://${config.realtime.endpoint}/mqtt?x-amz-customauthorizer-name=${config.realtime.authorizer}`;

    import("mqtt")
      .then(({ default: mqtt }) => {
        if (cancelled) return;
        client = mqtt.connect(url, {
          protocolVersion: 5,
          username: "",
          password: token ?? "dev",
          clientId: `skynet-web-${Math.random().toString(36).slice(2)}`,
          reconnectPeriod: 5000,
          connectTimeout: 15000,
        });
        client.on("connect", () => {
          if (cancelled) return;
          setStatus("connected");
          client?.subscribe(topic, { qos: 0 });
        });
        client.on("message", (_topic, payload) => {
          try {
            handler.current(JSON.parse(payload.toString()) as RealtimeEvent);
          } catch {
            // ignore malformed frames
          }
        });
        client.on("error", () => setStatus("error"));
        client.on("close", () => setStatus((s) => (s === "connected" ? "connecting" : s)));
      })
      .catch(() => setStatus("error"));

    return () => {
      cancelled = true;
      client?.end(true);
    };
  }, [token, channel]);

  return status;
}
